import { useCallback } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useQueryParamHelper } from '@shared/core/queryString';
import type { EventFeatureFlagKey } from './eventFeatureFlags.types';

export const useEventFeatureFlagsContext = () => {
  const { eventInfo, loading, ssrEventInfo } = useEventInfo();
  const { getValueString } = useQueryParamHelper();

  const isEventFeatureFlagEnabled = useCallback(
    (flag: EventFeatureFlagKey) => {
      // 1. Check URL Query Parameter: feature.${flag}
      const maybeQueryParamValue = getValueString(`feature.${flag}`);
      if (maybeQueryParamValue) {
        return maybeQueryParamValue === 'true';
      }

      // 2. Check event feature flags.
      //
      // SSR public-first optimization: `ssrEventInfo.featureFlags` comes from the server
      // preflight query (fetchFeatureFlagByEventName) and is seeded into EventInfoProvider
      // before BasicEventInfo resolves. This allows feature flag checks to resolve immediately
      // during SSR — unblocking queries like GuestSiteWebsiteHeaderSettings that depend on
      // `useWebsitePublishCapabilityFeature()` (which calls this hook).
      //
      // The preflight flags are only available during the initial load phase (before
      // BasicEventInfo resolves). Once BasicEventInfo resolves, `eventInfo.featureFlags`
      // takes over with the canonical data. The preflight flags are safe because they come
      // from the same backend source (eventByName.featureFlags) — just fetched earlier in
      // the SSR pipeline.
      return (ssrEventInfo?.featureFlags || eventInfo?.featureFlags)?.includes(flag) ?? false;
    },
    [eventInfo, getValueString, ssrEventInfo],
  );

  return {
    // If either ssrEventInfo or eventInfo has resolved feature flags (even an empty array []),
    // we're not loading. Only treat as loading when both are null/undefined AND BasicEventInfo
    // is still loading. An empty array means "resolved with no flags", not "still loading".
    loading: !(ssrEventInfo?.featureFlags ?? eventInfo?.featureFlags) && loading,
    isEventFeatureFlagEnabled,
  };
};
